/**
 * Profile Step - Optional step for authority profile information
 *
 * Allows authorities to enter additional information like organization name,
 * contact details, and address during registration.
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SkipNextIcon from '@mui/icons-material/SkipNext';

export interface ProfileData {
  organizationName?: string;
  department?: string;
  contactEmail?: string;
  streetAddress?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
  website?: string;
}

interface ProfileStepProps {
  /** Callback when profile data is submitted */
  onSubmit: (profile: ProfileData) => void;

  /** Callback when step is skipped */
  onSkip: () => void;

  /** Initial profile data (for editing) */
  initialData?: ProfileData;
}

export function ProfileStep({
  onSubmit,
  onSkip,
  initialData = {},
}: ProfileStepProps) {
  const [profile, setProfile] = useState<ProfileData>(initialData);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleChange = (field: keyof ProfileData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProfile((prev) => ({ ...prev, [field]: e.target.value || undefined }));
    if (field === 'contactEmail') {
      setEmailError(null);
    }
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email if provided
    if (profile.contactEmail && !validateEmail(profile.contactEmail)) {
      setEmailError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      return;
    }

    onSubmit(profile);
  };

  const hasAnyData = Object.values(profile).some((v) => v && v.trim() !== '');

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <BusinessIcon sx={{ fontSize: 48, color: 'primary.main' }} />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Optional: Geben Sie zusätzliche Informationen über Ihre Behörde an.
        Diese Informationen werden Bürgern angezeigt, die Anträge einreichen.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Alle Felder sind optional. Sie können diesen Schritt auch überspringen.
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Name der Behörde"
            value={profile.organizationName || ''}
            onChange={handleChange('organizationName')}
            placeholder="z.B. Stadtverwaltung Musterstadt"
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Abteilung"
            value={profile.department || ''}
            onChange={handleChange('department')}
            placeholder="z.B. Bürgeramt"
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            type="email"
            label="Kontakt-E-Mail"
            value={profile.contactEmail || ''}
            onChange={handleChange('contactEmail')}
            placeholder="kontakt@behoerde.de"
            error={!!emailError}
            helperText={emailError}
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Straße und Hausnummer"
            value={profile.streetAddress || ''}
            onChange={handleChange('streetAddress')}
            placeholder="Musterstraße 1"
            size="small"
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            fullWidth
            label="PLZ"
            value={profile.postalCode || ''}
            onChange={handleChange('postalCode')}
            placeholder="12345"
            size="small"
            inputProps={{ maxLength: 10 }}
          />
        </Grid>

        <Grid item xs={8}>
          <TextField
            fullWidth
            label="Stadt"
            value={profile.city || ''}
            onChange={handleChange('city')}
            placeholder="Musterstadt"
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Telefon"
            value={profile.phone || ''}
            onChange={handleChange('phone')}
            placeholder="+49 123 456789"
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Website"
            value={profile.website || ''}
            onChange={handleChange('website')}
            placeholder="https://www.behoerde.de"
            size="small"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onSkip}
          startIcon={<SkipNextIcon />}
        >
          Überspringen
        </Button>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!hasAnyData}
        >
          Weiter
        </Button>
      </Box>

      {!hasAnyData && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          sx={{ mt: 2 }}
        >
          Füllen Sie mindestens ein Feld aus oder klicken Sie auf "Überspringen"
        </Typography>
      )}
    </Box>
  );
}
